import httpClient from './httpClient';

const API_BASE_URL = '/tareos';

const tareoService = {
  // Obtener tareos con filtros
  obtenerTareos: async (filtros = {}) => {
    try {
      const params = new URLSearchParams();
      
      Object.keys(filtros).forEach(key => {
        if (filtros[key] !== null && filtros[key] !== undefined && filtros[key] !== '') {
          params.append(key, filtros[key]);
        }
      });

      const response = await httpClient.get(`${API_BASE_URL}?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Error al obtener tareos:', error);
      throw error;
    }
  },

  // Crear nuevo tareo
  crearTareo: async (tareoData) => {
    try {
      const response = await httpClient.post(API_BASE_URL, tareoData);
      return response.data;
    } catch (error) {
      console.error('Error al crear tareo:', error);
      throw error;
    }
  },

  // Crear múltiples tareos de forma masiva
  // tareosData puede ser array de tareos o { tareos: [], crear_autorizaciones_aprobadas?: boolean }
  crearTareoMasivo: async (tareosData) => {
    try {
      const payload = Array.isArray(tareosData) ? { tareos: tareosData } : tareosData;
      const tareos = payload.tareos || [];
      console.log('🚀 Enviando tareos masivos al backend:', tareos.length, 'tareos');
      console.log('📋 Payload completo:', payload);
      const muestra = (tareos.slice(0, 5)).map((t, i) => ({
        index: i,
        fecha: t.fecha,
        tipo_fecha: typeof t.fecha,
        empleado_id: t.empleado_id,
        proyecto_id: t.proyecto_id,
      }));
      console.log('📋 Muestra envío (primeros 5 tareos, campo fecha):', muestra);
      
      // Lotes grandes: el backend responde 202 y procesa en segundo plano
      const response = await httpClient.post(`${API_BASE_URL}/masivo`, payload, {
        timeout: tareos.length >= 500 ? 180000 : 120000,
      });
      
      console.log('✅ Respuesta del backend para registro masivo:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error al crear tareos masivos:', error);
      console.error('❌ Error response:', error.response?.data);
      
      // Proporcionar respuesta de error estructurada (incl. errores_actividad del backend)
      return {
        success: false,
        message: error.response?.data?.message || 'Error al crear tareos masivos',
        errors: error.response?.data?.errors || null,
        errores_actividad: error.response?.data?.errores_actividad || null,
        errores_tipo_evento: error.response?.data?.errores_tipo_evento || null,
        duplicados: error.response?.data?.duplicados || null,
      };
    }
  },

  /** Estado de importación masiva en segundo plano */
  obtenerEstadoImportacionMasiva: async (importacionId) => {
    try {
      const response = await httpClient.get(`${API_BASE_URL}/importaciones/${importacionId}`);
      return response.data;
    } catch (error) {
      console.error('Error al obtener estado de importación:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Error al consultar estado de importación',
      };
    }
  },

  // Regularizar conceptos: añadir conceptos a tareos ya existentes (no crea tareos nuevos)
  regularizarConceptos: async (items) => {
    try {
      const response = await httpClient.post(`${API_BASE_URL}/regularizar-conceptos`, { items });
      return response.data;
    } catch (error) {
      console.error('Error al regularizar conceptos:', error);
      const data = error.response?.data;
      return {
        success: false,
        message: data?.message || 'Error al regularizar conceptos',
        errors: data?.errors || null,
        data: data?.data || null
      };
    }
  },

  // Obtener tareo por ID
  obtenerTareo: async (id) => {
    try {
      const response = await httpClient.get(`${API_BASE_URL}/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error al obtener tareo:', error);
      throw error;
    }
  },

  // Actualizar tareo
  actualizarTareo: async (id, tareoData) => {
    try {
      const response = await httpClient.put(`${API_BASE_URL}/${id}`, tareoData);
      return response.data;
    } catch (error) {
      console.error('Error al actualizar tareo:', error);
      throw error;
    }
  },

  // Eliminar tareo
  eliminarTareo: async (id) => {
    try {
      const response = await httpClient.delete(`${API_BASE_URL}/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error al eliminar tareo:', error);
      throw error;
    }
  },

  // Eliminar múltiples tareos de forma masiva
  eliminarTareoMasivo: async (ids) => {
    try {
      console.log('🗑️ Enviando borrado masivo de tareos al backend:', ids.length, 'tareos');
      console.log('📋 IDs a eliminar:', ids);
      
      const response = await httpClient.delete(`${API_BASE_URL}/masivo/delete`, {
        data: { ids: ids }
      });
      
      console.log('✅ Respuesta del backend para borrado masivo:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error al eliminar tareos masivos:', error);
      console.error('❌ Error response:', error.response?.data);
      throw error;
    }
  },

  // Obtener resumen de asistencia
  obtenerResumen: async (filtros) => {
    try {
      const params = new URLSearchParams();
      
      Object.keys(filtros).forEach(key => {
        if (filtros[key] !== null && filtros[key] !== undefined && filtros[key] !== '') {
          params.append(key, filtros[key]);
        }
      });

      const response = await httpClient.get(`${API_BASE_URL}/resumen?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Error al obtener resumen:', error);
      throw error;
    }
  },

  // Obtener empleados de una empresa
  obtenerEmpleados: async (empresaId, filtros = {}) => {
    try {
      const params = new URLSearchParams();
      Object.keys(filtros).forEach(key => {
        if (filtros[key] !== null && filtros[key] !== undefined && filtros[key] !== '') {
          params.append(key, filtros[key]);
        }
      });

      const queryString = params.toString();
      const response = await httpClient.get(
        `${API_BASE_URL}/empleados/${empresaId}${queryString ? `?${queryString}` : ''}`
      );
      return response.data;
    } catch (error) {
      console.error('Error al obtener empleados:', error);
      throw error;
    }
  },

  // Obtener proyectos de una empresa
  obtenerProyectos: async (empresaId) => {
    try {
      const response = await httpClient.get(`${API_BASE_URL}/proyectos/${empresaId}`);
      return response.data;
    } catch (error) {
      console.error('Error al obtener proyectos:', error);
      throw error;
    }
  },

  // Obtener configuración de tareo de una empresa
  obtenerConfiguracionTareo: async (empresaId) => {
    try {
      const response = await httpClient.get(`${API_BASE_URL}/configuracion/${empresaId}`);
      return response.data;
    } catch (error) {
      console.error('Error al obtener configuración de tareo:', error);
      throw error;
    }
  },

  // Obtener tareos por rango de fechas (para planillas)
  obtenerTareosPorPeriodo: async (fechaInicio, fechaFin, empresaId) => {
    try {
      console.log('🔄 === OBTENIENDO TAREOS POR PERÍODO ===');
      console.log('📅 Fecha Inicio:', fechaInicio);
      console.log('📅 Fecha Fin:', fechaFin);
      console.log('🏢 Empresa ID:', empresaId);

      const params = new URLSearchParams();
      params.append('fecha_inicio', fechaInicio);
      params.append('fecha_fin', fechaFin);
      params.append('empresa_id', empresaId);

      const response = await httpClient.get(`${API_BASE_URL}/periodo?${params.toString()}`);
      
      console.log('📨 Respuesta tareos por período:', response.data);
      console.log('✅ Response.success:', response.data?.success);
      console.log('📊 Response.data:', response.data?.data);
      console.log('📈 Total tareos encontrados:', response.data?.data?.length || 0);

      return response.data;
    } catch (error) {
      console.error('❌ Error al obtener tareos por período:', error);
      console.error('❌ Error message:', error.message);
      console.error('❌ Error stack:', error.stack);
      throw error;
    }
  },

  // Obtener resumen de tareos por período (estadísticas)
  obtenerResumenPorPeriodo: async (fechaInicio, fechaFin, empresaId) => {
    try {
      console.log('🔄 === OBTENIENDO RESUMEN DE TAREOS POR PERÍODO ===');
      console.log('📅 Fecha Inicio:', fechaInicio);
      console.log('📅 Fecha Fin:', fechaFin);
      console.log('🏢 Empresa ID:', empresaId);

      const params = new URLSearchParams();
      params.append('fecha_inicio', fechaInicio);
      params.append('fecha_fin', fechaFin);
      params.append('empresa_id', empresaId);

      const response = await httpClient.get(`${API_BASE_URL}/resumen-periodo?${params.toString()}`, {
        timeout: 180000,
      });
      
      console.log('📨 Respuesta resumen tareos por período:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error al obtener resumen de tareos por período:', error);
      if (!error.response && (error.code === 'ECONNABORTED' || error.message === 'Network Error')) {
        const timeoutErr = new Error(
          'El reporte tardó demasiado o el servidor no respondió. Prueba un rango más corto o vuelve a intentar en unos minutos.'
        );
        timeoutErr.isReporteTimeout = true;
        throw timeoutErr;
      }
      throw error;
    }
  },

  // Funciones auxiliares para el frontend
  
  // Formatear hora para envío al backend
  formatearHora: (hora) => {
    if (!hora) return null;
    
    // Si ya está en formato HH:MM, devolverlo tal como está
    if (typeof hora === 'string' && hora.match(/^\d{2}:\d{2}$/)) {
      return hora;
    }
    
    // Si es un objeto Date, extraer la hora
    if (hora instanceof Date) {
      return hora.toTimeString().slice(0, 5);
    }
    
    return null;
  },

  // Calcular horas trabajadas
  calcularHorasTrabajadas: (horaEntrada, horaSalida) => {
    if (!horaEntrada || !horaSalida) return 0;

    const [horasEntrada, minutosEntrada] = horaEntrada.split(':').map(Number);
    const [horasSalida, minutosSalida] = horaSalida.split(':').map(Number);

    const minutosEntradaTotal = horasEntrada * 60 + minutosEntrada;
    const minutosSalidaTotal = horasSalida * 60 + minutosSalida;

    let minutosTrabajados = minutosSalidaTotal - minutosEntradaTotal;
    
    // Manejar caso donde se cruza medianoche
    if (minutosTrabajados < 0) {
      minutosTrabajados += 24 * 60;
    }

    let horasTrabajadas = minutosTrabajados / 60;

    // Descanso automático de 1 hora si trabajó más de 6 horas
    if (horasTrabajadas > 6) {
      horasTrabajadas -= 1;
    }

    return Math.max(0, Math.round(horasTrabajadas * 100) / 100);
  },

  // Obtener color del badge según tipo de evento
  obtenerColorEvento: (tipoEvento) => {
    const colores = {
      'asistio': 'bg-green-100 text-green-800',
      'falta': 'bg-red-100 text-red-800',
      'feriado': 'bg-purple-100 text-purple-800',
      'feriado_laborado': 'bg-purple-100 text-purple-800',
      'descanso': 'bg-blue-100 text-blue-800',
      'descanso_laborado': 'bg-blue-100 text-blue-800',
      'descanso_medico': 'bg-yellow-100 text-yellow-800',
      'reposo_medico': 'bg-orange-50 text-orange-700',
      'subsidio_por_enfermedad': 'bg-orange-200 text-orange-900',
      'licencia_con_goce_de_haber': 'bg-indigo-100 text-indigo-800'
    };

    return colores[tipoEvento] || 'bg-gray-100 text-gray-800';
  },

  // Obtener texto legible del tipo de evento
  obtenerTextoEvento: (tipoEvento) => {
    const textos = {
      'asistio': 'Asistió',
      'falta': 'Falta',
      'feriado': 'Feriado',
      'feriado_laborado': 'Feriado Laborado',
      'descanso': 'Descanso',
      'descanso_laborado': 'Descanso Laborado',
      'descanso_medico': 'Descanso Médico',
      'subsidio_por_enfermedad': 'Subsidio por Enfermedad',
      'licencia_con_goce_de_haber': 'Licencia con Goce de Haber'
    };

    return textos[tipoEvento] || tipoEvento;
  },

  // Validar datos del tareo antes de enviar
  validarTareo: (tareoData) => {
    const errores = [];

    if (!tareoData.empresa_id) {
      errores.push('La empresa es requerida');
    }

    if (!tareoData.empleado_id) {
      errores.push('El empleado es requerido');
    }

    if (!tareoData.proyecto_id) {
      errores.push('El proyecto es requerido');
    }

    if (!tareoData.fecha) {
      errores.push('La fecha es requerida');
    }

    if (!tareoData.tipo_evento) {
      errores.push('El tipo de evento es requerido');
    }

    if (!tareoData.hora_entrada_teorica) {
      errores.push('La hora de entrada teórica es requerida');
    }

    if (!tareoData.hora_salida_teorica) {
      errores.push('La hora de salida teórica es requerida');
    }

    // Validar formato de horas
    const formatoHora = /^\d{2}:\d{2}$/;
    
    if (tareoData.hora_entrada_teorica && !formatoHora.test(tareoData.hora_entrada_teorica)) {
      errores.push('Formato de hora de entrada teórica inválido (HH:MM)');
    }

    if (tareoData.hora_salida_teorica && !formatoHora.test(tareoData.hora_salida_teorica)) {
      errores.push('Formato de hora de salida teórica inválido (HH:MM)');
    }

    if (tareoData.hora_entrada_real && !formatoHora.test(tareoData.hora_entrada_real)) {
      errores.push('Formato de hora de entrada real inválido (HH:MM)');
    }

    if (tareoData.hora_salida_real && !formatoHora.test(tareoData.hora_salida_real)) {
      errores.push('Formato de hora de salida real inválido (HH:MM)');
    }

    // Validar conceptos si existen
    if (tareoData.conceptos && Array.isArray(tareoData.conceptos)) {
      tareoData.conceptos.forEach((concepto, index) => {
        if (!concepto.plame_configuracion_id) {
          errores.push(`Concepto ${index + 1}: ID de configuración PLAME requerido`);
        }

        if (!concepto.tipo_concepto) {
          errores.push(`Concepto ${index + 1}: Tipo de concepto requerido`);
        }

        if (!concepto.monto || concepto.monto < 0) {
          errores.push(`Concepto ${index + 1}: Monto debe ser mayor a 0`);
        }
      });
    }

    return {
      esValido: errores.length === 0,
      errores
    };
  },

  // Preparar datos para envío al backend
  prepararDatosParaEnvio: (formData) => {
    const datos = {
      empresa_id: formData.empresa_id,
      empleado_id: formData.empleado_id,
      proyecto_id: formData.proyecto_id,
      fecha: formData.fecha,
      tipo_evento: formData.tipo_evento,
      hora_entrada_teorica: tareoService.formatearHora(formData.hora_entrada_teorica),
      hora_salida_teorica: tareoService.formatearHora(formData.hora_salida_teorica),
      hora_entrada_real: tareoService.formatearHora(formData.hora_entrada_real),
      hora_salida_real: tareoService.formatearHora(formData.hora_salida_real),
      observaciones: formData.observaciones || null
    };

    // Agregar conceptos si existen
    if (formData.conceptos && Array.isArray(formData.conceptos) && formData.conceptos.length > 0) {
      datos.conceptos = formData.conceptos.map(concepto => ({
        plame_configuracion_id: concepto.plame_configuracion_id,
        tipo_concepto: concepto.tipo_concepto,
        monto: parseFloat(concepto.monto),
        observaciones: concepto.observaciones || null
      }));
    }

    return datos;
  }
};

export default tareoService; 